import { Component, OnInit, ChangeDetectionStrategy, DestroyRef, inject, signal, computed, input } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  combineLatest,
  EMPTY,
  Observable,
  of,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { ModalButtons, ButtonTheme, ButtonMode } from '@fcom/ui-components';
import { ProfileType, ProfileTier, LoginMemberStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { AppState, RootPaths } from '@fcom/core';
import { loginToken, profileType, profile } from '@fcom/core/selectors/login.selector';
import { finShare } from '@fcom/rx';

import { Consent, ConsentGroup, ConsentStatus, ConsentTextId } from '../../interfaces/consents.interface';
import { GaContext } from '../../interfaces/gtm.interface';
import { ConsentService, ConsentToken, ConsentTokenType } from '../services/consent.service';
import { MarketingConsentTrackingService } from '../../gtm/services/marketing-consent-tracking.service';
import { consentPreferencesToken } from './marketing-preferences-utils';

export enum ConsentApiStatus {
  NOT_LOADED = 'NOT_LOADED',
  LOADED = 'LOADED',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
}

@Component({
  selector: 'fin-marketing-preferences',
  styleUrls: ['marketing-preferences.component.scss'],
  templateUrl: 'marketing-preferences.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MarketingPreferencesComponent implements OnInit {
  private consentService = inject(ConsentService);
  private store$ = inject<Store<AppState>>(Store);
  private languageService = inject(LanguageService);
  private route = inject(ActivatedRoute);
  router = inject(Router);
  private fb = inject(UntypedFormBuilder);
  private destroyRef = inject(DestroyRef);
  private marketingConsentTracking = inject(MarketingConsentTrackingService);

  marketingPreferencesForm: UntypedFormGroup;

  modalOpen = signal(false);
  consentApiStatus = signal<ConsentApiStatus>(ConsentApiStatus.NOT_LOADED);

  private token$: Observable<ConsentToken>;
  private consentGroup$: Observable<ConsentGroup>;

  consentGroup = signal<ConsentGroup | undefined>(undefined);

  consentLayoutGroups = computed(() => {
    const consentGroup = this.consentGroup();
    if (!consentGroup?.consents) {
      return [];
    }
    return this.consentService.getMarketingConsentLayoutGroups(consentGroup.consents);
  });

  successButtonUrl = toSignal(
    this.languageService.translate('plus.memberMarketingPreferences.successButtonLink.url').pipe(
      distinctUntilChanged(),
      filter((v) => !!v)
    ),
    { requireSync: false }
  );
  errorButtonUrl = toSignal(
    this.languageService.translate('plus.memberMarketingPreferences.errorButtonLink.url').pipe(
      distinctUntilChanged(),
      filter((v) => !!v)
    ),
    { requireSync: false }
  );

  tokenFromUrl = signal(false);

  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ConsentApiStatus = ConsentApiStatus;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly ModalButtons = ModalButtons;
  readonly ConsentTextId = ConsentTextId;

  token: string;

  useModal = input<boolean>(true);
  infoModalOpen = signal(false);

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get(consentPreferencesToken);

    let accountType$: Observable<ProfileType>;

    if (token) {
      this.tokenFromUrl.set(true);
      this.token$ = of({ token, type: ConsentTokenType.CAT });
      accountType$ = of(ProfileType.FPLUS);

      if (this.useModal()) {
        this.router.navigate([`/${this.languageService.langValue}/consent`], {
          relativeTo: this.route,
          queryParams: {
            [consentPreferencesToken]: token,
          },
          queryParamsHandling: 'merge',
        });
      }
    } else {
      this.tokenFromUrl.set(false);
      this.token$ = this.store$.pipe(
        loginToken(),
        filter((v) => !!v),
        take(1),
        map((v) => ({
          token: v,
        }))
      );
      accountType$ = this.store$.pipe(
        profileType(),
        filter((v) => !!v),
        take(1)
      );
    }

    this.consentGroup$ = combineLatest([this.token$, accountType$]).pipe(
      switchMap(([authHeaderValue, type]) => {
        return this.consentService.getMarketingConsents(authHeaderValue, type);
      }),
      filter((v) => !!v),
      finShare(),
      catchError(() => {
        this.consentApiStatus.set(ConsentApiStatus.ERROR);
        return EMPTY;
      })
    );

    this.consentGroup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((consentGroup) => {
      this.consentGroup.set(consentGroup);
      this.createMarketingConsentForm(consentGroup.consents);
      this.consentApiStatus.set(ConsentApiStatus.LOADED);
    });

    if (this.useModal()) {
      const isPrivacyPolicyPage$ = this.languageService.translate('plus.memberMarketingPreferences.disclaimer').pipe(
        distinctUntilChanged(),
        filter((v) => !!v),
        map((htmlString) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlString, 'text/html');
          const anchor = doc.querySelector('a');
          return anchor?.getAttribute('href') || null;
        }),
        map((ppLink) => this.router.url.includes(ppLink || ''))
      );

      combineLatest([isPrivacyPolicyPage$, this.store$.pipe(profile(), take(1))])
        .pipe(
          filter(([_, currentProfile]) => !!currentProfile),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(([isPrivacyPolicyPage, currentProfile]) => {
          const isPendingAccount =
            currentProfile.tier === ProfileTier.ACCOUNT && currentProfile.memberStatus === LoginMemberStatus.PENDING;
          if (isPendingAccount) {
            this.sendConsents();
            return;
          }

          const inBookingFlow = this.router.url.includes(`/${RootPaths.BOOKING_ROOT}/`);

          if (!isPrivacyPolicyPage && !inBookingFlow) {
            this.checkReconsent();
          }
        });
    }
  }

  checkReconsent(): void {
    this.consentGroup$
      .pipe(
        filter((group) => group.consents.some((a) => a.reConsentRequired === true)),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.modalOpen.set(true);
      });
  }

  sendConsents(): void {
    const formData = this.marketingPreferencesForm;
    const consentGroup = this.consentGroup();

    if (!consentGroup) {
      return;
    }

    const updatedConsents: Consent[] = consentGroup.consents.map((c) => ({
      ...c,
      consentStatus: formData?.value[c.consentTextId] ? ConsentStatus.ACCEPTED : ConsentStatus.DECLINED,
    }));

    setTimeout(() => {
      this.consentApiStatus.set(ConsentApiStatus.NOT_LOADED);
    }, 0);

    this.token$
      .pipe(
        take(1),
        switchMap((token: ConsentToken) => {
          if (!this.tokenFromUrl()) {
            this.consentApiStatus.set(ConsentApiStatus.SAVED);
            this.closeModal();
          }
          return this.consentService
            .sendConsents(token, updatedConsents)
            .pipe(tap(() => this.trackMarketingConsentChange(consentGroup.consents, updatedConsents)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.consentApiStatus.set(ConsentApiStatus.SAVED);
      });
  }

  private trackMarketingConsentChange(previousConsents: Consent[], updatedConsents: Consent[]): void {
    this.marketingConsentTracking.writeConsentCookie(updatedConsents);

    const previousById = new Map(previousConsents.map((c) => [c.consentTextId, c]));
    this.marketingConsentTracking.trackConsentChange({
      context: GaContext.SERVICE_FORMS,
      id: this.useModal() ? 'marketing-preferences-modal' : 'communication-preferences-page',
      changes: updatedConsents.map((current) => ({
        previous: previousById.get(current.consentTextId),
        current,
      })),
    });
  }

  closeModal(): void {
    this.modalOpen.set(false);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [consentPreferencesToken]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onClick(url: string): void {
    this.modalOpen.set(false);
    this.router.navigateByUrl(url);
  }

  private createMarketingConsentForm(consents: Consent[]) {
    const consentFields = consents.reduce((fields, consent) => {
      fields[consent.consentTextId] = this.fb.control(consent.consentStatus === 'accepted', { nonNullable: true });
      return fields;
    }, {});
    this.marketingPreferencesForm = this.fb.group({
      ...consentFields,
    });
  }
}
